import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";

import {
  type AdminNavSearchEntry,
  getAdminNavSearchEntries,
} from "@/routing/adminNavModelQueries";
import type { AdminRouteAccessArgs } from "@/routing/adminRoutes";
import { requestBroadcastExit } from "@/routing/broadcastLeaveGuard";
import { useShellDismiss } from "@/vnext/app-shell/useShellDismiss";
import { cx } from "@/vnext/shared/cx";

const MAX_RESULTS = 8;
const RECORD_SEARCH_OPTION_ID = "records";

/** F-202: names containing the text, best match (starting with it) first. */
export const findSidebarSearchResults = (
  entries: readonly AdminNavSearchEntry[],
  query: string,
): AdminNavSearchEntry[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  return entries
    .map((entry, order) => ({
      entry,
      order,
      at: entry.label.toLowerCase().indexOf(needle),
    }))
    .filter((match) => match.at >= 0)
    .sort((left, right) => left.at - right.at || left.order - right.order)
    .slice(0, MAX_RESULTS)
    .map((match) => match.entry);
};

function HighlightedName({ label, query }: { label: string; query: string }) {
  const at = label.toLowerCase().indexOf(query.trim().toLowerCase());
  if (at < 0) return <>{label}</>;
  const end = at + query.trim().length;
  return (
    <>
      {label.slice(0, at)}
      <span className="vnext-shell-search__match">{label.slice(at, end)}</span>
      {label.slice(end)}
    </>
  );
}

/**
 * The sidebar search (F-007, F-008, F-202, F-203) over the navigation map's
 * page and tab index. Record search stays behind `search.view`: that admin
 * also gets a last row that opens the record search with the same text.
 */
export function ShellSidebarSearch({
  canSearchRecords,
  collapsed,
  hiddenPaths,
  onSearchRecords,
  routeAccess,
}: {
  canSearchRecords: boolean;
  collapsed: boolean;
  hiddenPaths?: ReadonlySet<string>;
  onSearchRecords: (query: string) => void;
  routeAccess: AdminRouteAccessArgs;
}) {
  const navigate = useNavigate();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [besideOpen, setBesideOpen] = useState(false);
  const shortcutHint =
    typeof navigator !== "undefined" &&
    navigator.platform.toLowerCase().includes("mac")
      ? "⌘K"
      : "Ctrl+K";

  const entries = useMemo(
    () =>
      getAdminNavSearchEntries(routeAccess).filter(
        (entry) => !hiddenPaths?.has(entry.path),
      ),
    [hiddenPaths, routeAccess],
  );
  const results = useMemo(
    () => findSidebarSearchResults(entries, query),
    [entries, query],
  );
  const showRecordOption = canSearchRecords && query.trim().length >= 2;
  const optionCount = results.length + (showRecordOption ? 1 : 0);
  const open = query.trim().length > 0;
  const boxVisible = !collapsed || besideOpen;

  const reset = useCallback(() => {
    setQuery("");
    setHighlight(0);
    setBesideOpen(false);
  }, []);

  useShellDismiss({
    open: open || besideOpen,
    onClose: reset,
    refs: useMemo(() => [rootRef], []),
  });

  const focusRequested = useRef(false);
  const focusInputIfRequested = () => {
    if (!focusRequested.current || !inputRef.current) return;
    focusRequested.current = false;
    inputRef.current.focus();
  };
  // F-203: ⌘K puts the cursor in the box; collapsed, it opens beside the rail.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k")
        return;
      event.preventDefault();
      // Collapsed, the box renders only after this state change: ask for
      // focus and give it once the input exists (a timer can run first).
      focusRequested.current = true;
      if (collapsed) setBesideOpen(true);
      else focusInputIfRequested();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [collapsed]);
  useEffect(() => {
    focusInputIfRequested();
  });

  const choose = (index: number) => {
    if (index < results.length) {
      const path = results[index].path;
      reset();
      void requestBroadcastExit(() => navigate(path));
      return;
    }
    if (showRecordOption) {
      const text = query.trim();
      reset();
      onSearchRecords(text);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      reset();
      return;
    }
    if (!open || optionCount === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((current) => (current + 1) % optionCount);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((current) => (current - 1 + optionCount) % optionCount);
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(Math.min(highlight, optionCount - 1));
    }
  };

  const optionId = (index: number) =>
    index < results.length
      ? `${listId}-${index}`
      : `${listId}-${RECORD_SEARCH_OPTION_ID}`;

  return (
    <div
      className={cx(
        "vnext-shell-search",
        collapsed && "vnext-shell-search--collapsed",
        besideOpen && "is-beside-open",
      )}
      ref={rootRef}
    >
      {collapsed ? (
        <button
          aria-label="Search pages"
          className="vnext-shell-rail-button"
          data-tooltip="Search"
          onClick={() => {
            focusRequested.current = !besideOpen;
            setBesideOpen((current) => !current);
          }}
          type="button"
        >
          <SearchOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
        </button>
      ) : null}
      {boxVisible ? (
        <div className="vnext-shell-search__box">
          <SearchOutlinedIcon
            aria-hidden="true"
            className="vnext-shell-search__icon"
            sx={{ fontSize: 13 }}
          />
          <input
            aria-activedescendant={
              open && optionCount > 0
                ? optionId(Math.min(highlight, optionCount - 1))
                : undefined
            }
            aria-autocomplete="list"
            aria-controls={listId}
            aria-expanded={open}
            aria-label="Search pages"
            className="vnext-shell-search__input"
            onChange={(event) => {
              setQuery(event.target.value);
              setHighlight(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Search"
            ref={inputRef}
            role="combobox"
            type="text"
            value={query}
          />
          <span aria-hidden="true" className="vnext-shell-search__hint">
            {shortcutHint}
          </span>
        </div>
      ) : null}
      {open && boxVisible ? (
        <div
          className="vnext-shell-search__dropdown"
          id={listId}
          role="listbox"
        >
          {results.map((entry, index) => (
            // Options are chosen from the combobox's keyboard handler
            // (aria-activedescendant); the click is the pointer path.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              // A page named after its section (Dashboard) says it once.
              aria-label={
                entry.location === entry.label
                  ? entry.label
                  : `${entry.label}, ${entry.location}`
              }
              aria-selected={index === highlight}
              className={cx(
                "vnext-shell-search__option",
                index === highlight && "is-highlighted",
              )}
              id={optionId(index)}
              key={entry.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(index)}
              onMouseEnter={() => setHighlight(index)}
              role="option"
            >
              <span className="vnext-shell-search__name">
                <HighlightedName label={entry.label} query={query} />
              </span>
              {entry.location === entry.label ? null : (
                <span className="vnext-shell-search__location">
                  {entry.location}
                </span>
              )}
            </div>
          ))}
          {results.length === 0 ? (
            <div className="vnext-shell-search__empty" role="status">
              {`Nothing called "${query.trim()}"`}
            </div>
          ) : null}
          {showRecordOption ? (
            // Keyboard choice goes through the combobox, as for the pages.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              aria-selected={highlight === results.length}
              className={cx(
                "vnext-shell-search__option vnext-shell-search__option--records",
                highlight === results.length && "is-highlighted",
              )}
              id={optionId(results.length)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(results.length)}
              onMouseEnter={() => setHighlight(results.length)}
              role="option"
            >
              {`Search records for "${query.trim()}"`}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
